import { useEffect, useRef, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Skeleton from "@mui/material/Skeleton";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { useCompanyPageStore } from "../../../../../../store/companyPageStore";
import { SearchToggle } from "@/components/ui/SearchToggle";
import { InstallSectionCard } from "../InstallSectionCard";
import { EmptyState } from "../../../../components/EmptyState";
import { ExportChart } from "../ExportChart";
import { useQuery } from "@apollo/client";
import {
  COMPANY_INSTALL_VENDORS_BY_CATEGORY_QUERY,
  CompanyInstallsVendorsByCategoryPayload,
} from "../../../../../../legacy/queries/Installs";
import { COMPANY_SEARCH_CLIENT } from "../../../../../../utils/consts";

type ViewMode = "10" | "20" | "50";

export const VendorsByCategory = () => {
  const company = useCompanyPageStore((state) => state.company);
  const installFilters = useCompanyPageStore((state) => state.installFilters);
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<am4charts.XYChart | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("20");
  const [searchTerm, setSearchTerm] = useState("");

  const { data, loading } = useQuery<CompanyInstallsVendorsByCategoryPayload>(
    COMPANY_INSTALL_VENDORS_BY_CATEGORY_QUERY,
    {
      variables: {
        hgId: company!.hgId,
        isRolledUp: false,
        countryAlpha2s: installFilters.countryAlpha2s,
        productIds: installFilters.productIds,
        vendorIds: installFilters.vendorIds,
        productCategoryIds: installFilters.productCategoryIds,
        productAttributeIds: installFilters.productAttributeIds,
        page: 0,
        // Reasonable default to get all data
        per: 300,
      },
      context: {
        clientName: COMPANY_SEARCH_CLIENT,
      },
    }
  );

  const topCount = parseInt(viewMode);

  const chartData = useMemo(() => {
    let filteredData = data?.installVendorCountByCategory?.data || [];

    // Apply search filter
    if (searchTerm.trim()) {
      filteredData = filteredData.filter((item) =>
        item.category.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filteredData.slice(0, topCount).map((item) => ({
      category:
        item.category.name.length > 25
          ? item.category.name.substring(0, 25) + "..."
          : item.category.name,
      fullName: item.category.name,
      nameTree: item.category.nameTree.join(" > "),
      vendors: item.vendorCount,
    }));
  }, [data?.installVendorCountByCategory?.data, topCount, searchTerm]);

  const csvExportData = useMemo(() => {
    return chartData.map((item) => ({
      category: item.fullName,
      vendorCount: item.vendors,
    }));
  }, [chartData]);

  // Effect for chart creation (runs once when chartRef is available)
  useEffect(() => {
    if (!chartRef.current || chartInstanceRef.current) {
      return;
    }

    const chart = am4core.create(chartRef.current, am4charts.XYChart);
    chartInstanceRef.current = chart;

    // Create category axis
    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = "category";
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.minGridDistance = 25;
    categoryAxis.renderer.labels.template.rotation = -45;
    categoryAxis.renderer.labels.template.horizontalCenter = "right";
    categoryAxis.renderer.labels.template.verticalCenter = "middle";
    categoryAxis.renderer.labels.template.fontSize = 12;
    categoryAxis.renderer.labels.template.tooltipText = "{nameTree}";

    if (categoryAxis.tooltip) {
      categoryAxis.tooltip.disabled = false;
      categoryAxis.cursorTooltipEnabled = false;

      categoryAxis.tooltip.maxWidth = 250;
      if (categoryAxis.tooltip.label) {
        categoryAxis.tooltip.label.wrap = true;
      }
    }

    // Style X axis grid lines
    categoryAxis.renderer.grid.template.disabled = true;

    // Create value axis
    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.title.text = "Number of Vendors";
    valueAxis.renderer.minWidth = 50;

    // Style Y axis grid lines
    valueAxis.renderer.grid.template.stroke = am4core.color("#000000");
    valueAxis.renderer.grid.template.strokeWidth = 1;
    valueAxis.renderer.grid.template.strokeDasharray = "3,3";

    // Create series
    const series = chart.series.push(new am4charts.ColumnSeries());
    series.dataFields.valueY = "vendors";
    series.dataFields.categoryX = "category";
    series.name = "Vendors";
    series.tooltipText = "{fullName}: [bold]{valueY}[/] vendors"; // Tooltip da SÉRIE (barra)
    series.fillOpacity = 0.8;

    // Style columns with fixed color
    const columnTemplate = series.columns.template;
    columnTemplate.strokeWidth = 0;
    columnTemplate.strokeOpacity = 1;
    columnTemplate.fill = am4core.color("#81d4fa");

    // Add hover state
    const hoverState = columnTemplate.states.create("hover");
    hoverState.properties.fillOpacity = 0.9;
    hoverState.properties.fill = am4core.color("#4fc3f7");

    // Add cursor (tooltip only, no pan, no lines)
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.behavior = "none";
    chart.cursor.xAxis = categoryAxis;
    chart.cursor.snapToSeries = series;
    chart.cursor.lineX.strokeOpacity = 0;
    chart.cursor.lineY.strokeOpacity = 0;

    // Especificamente para o tooltip do cursor no eixo X (para não mostrar um tooltip adicional para o eixo):
    if (chart.cursor.tooltip) {
      chart.cursor.tooltip.disabled = true;
    }
    // Set data
    chart.data = chartData;

    return () => {
      chart.dispose();
      chartInstanceRef.current = null;
    };
  }, [chartData]);

  // Effect for data updates (runs when chartData changes)
  useEffect(() => {
    if (!chartInstanceRef.current || loading) {
      return;
    }

    chartInstanceRef.current.data = chartData;
  }, [chartData, loading]);

  const handleViewModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newViewMode: ViewMode | null
  ) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const hasData = !loading && chartData.length > 0;
  const isEmpty = !loading && chartData.length === 0;

  const actions = (
    <>
      <SearchToggle
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        placeholder="Search category"
        width={200}
      />

      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={handleViewModeChange}
        size="small"
      >
        <ToggleButton value="10">Top 10</ToggleButton>
        <ToggleButton value="20">Top 20</ToggleButton>
        <ToggleButton value="50">Top 50</ToggleButton>
      </ToggleButtonGroup>

      <ExportChart
        chartInstanceRef={chartInstanceRef}
        data={csvExportData}
        filename="vendors-by-category"
      />
    </>
  );

  return (
    <InstallSectionCard
      title="Vendors by Category"
      actions={actions}
      filterKey="VENDORS_BY_CATEGORY"
    >
      <Box
        ref={chartRef}
        sx={{
          height: 360,
          width: "100%",
          display: hasData ? "block" : "none",
        }}
      />

      {loading && (
        <Box sx={{ height: 360 }}>
          <Skeleton variant="rectangular" width="100%" height="100%" />
        </Box>
      )}

      {isEmpty && (
        <EmptyState
          description={
            searchTerm.trim()
              ? `No categories match "${searchTerm}"`
              : "No data available for this company at this time"
          }
        />
      )}
    </InstallSectionCard>
  );
};
